// Download helpers for KPI-style widgets (kpi, sparkle). Everything the API
// sent — value, formula, counts, comparison, trend points — goes into the
// data exports; the image exports capture the rendered widget card.

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { createRoot } from "react-dom/client";
import type { ReactNode } from "react";

// ─── Generic KPI payload shape (all fields optional) ─────────────────────────

export type KpiExportData = {
  label?: string;
  total_count?: number | string;
  case_id?: string | number;
  case_ids?: Array<string | number>;
  case_list?: Array<{
    case_id?: string | number;
    status_name?: string | null;
    received_date?: string | null;
  }>;
  value?: number | string;
  value_type?: string;
  formula?: string;
  subtitle?: string;
  average?: number;
  avg_percentage?: string;
  trend_label?: string;
  covered_count?: number;
  referral_count?: number;
  comparison?: {
    prior_value?: number | string | null;
    change?: number | string | null;
    change_percent?: number | string | null;
    trend?: string | null;
  } | null;
  spark?: {
    period?: string;
    value_type?: string;
    average?: number;
    points?: Array<Record<string, unknown>>;
  };
} & Record<string, unknown>;

// ─── Filenames ────────────────────────────────────────────────────────────────

export const exportFileName = (label?: string): string => {
  const slug =
    (label ?? "kpi")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "kpi";
  return `${slug}-${new Date().toISOString().slice(0, 10)}`;
};

// ─── Download plumbing ────────────────────────────────────────────────────────

const triggerDownload = (href: string, filename: string) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// ─── CSV ──────────────────────────────────────────────────────────────────────

const csvCell = (val: unknown): string => {
  const s = val === null || val === undefined ? "" : String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const isScalar = (v: unknown): v is string | number | boolean =>
  ["string", "number", "boolean"].includes(typeof v);

const titleCase = (key: string): string =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bPa\b/g, "PA");

// Builds two CSV sections: a Metric/Value summary of every scalar field
// (including nested comparison.*), then a table of the trend points with
// every key the API provided.
export const buildKpiCsv = (
  data: KpiExportData,
  title?: string,
  description?: string | null,
): string => {
  const lines: string[] = [];
  const summary: Array<[string, unknown]> = [];

  if (title) summary.push(["Widget", title]);
  if (description) summary.push(["Description", description]);

  // Preferred order first, then any remaining scalar fields the API added.
  const preferred = [
    "label",
    "total_count",
    "value",
    "value_type",
    "formula",
    "subtitle",
    "covered_count",
    "referral_count",
    "average",
    "avg_percentage",
    "trend_label",
  ];
  const seen = new Set<string>([
    "comparison",
    "spark",
    "case_id",
    "case_ids",
  ]);
  preferred.forEach((key) => {
    seen.add(key);
    if (isScalar(data[key])) summary.push([titleCase(key), data[key]]);
  });
  Object.keys(data).forEach((key) => {
    if (!seen.has(key) && isScalar(data[key])) {
      summary.push([titleCase(key), data[key]]);
    }
  });

  const cmp = data.comparison;
  if (cmp) {
    Object.entries(cmp).forEach(([key, val]) => {
      if (isScalar(val as unknown))
        summary.push([`Prior Comparison — ${titleCase(key)}`, val]);
    });
  }

  lines.push("Metric,Value");
  summary.forEach(([k, v]) => lines.push(`${csvCell(k)},${csvCell(v)}`));

  const points = data.spark?.points ?? [];
  if (points.length > 0) {
    const cols = Array.from(
      points.reduce((set, p) => {
        Object.keys(p).forEach((k) => set.add(k));
        return set;
      }, new Set<string>()),
    );
    lines.push("");
    lines.push(
      `Trend (${data.spark?.period ?? "period"})${data.spark?.average !== undefined ? ` — average ${data.spark.average}` : ""}`,
    );
    lines.push(cols.map((c) => csvCell(titleCase(c))).join(","));
    points.forEach((p) => {
      lines.push(cols.map((c) => csvCell(p[c])).join(","));
    });
  }

  // BOM so Excel opens UTF-8 correctly
  return "\uFEFF" + lines.join("\r\n");
};

export const getKpiCaseIds = (
  data: KpiExportData,
): Array<string | number> =>
  Array.isArray(data.case_ids)
    ? data.case_ids
    : data.case_id !== undefined && data.case_id !== null
      ? [data.case_id]
      : [];

export const getKpiCaseListRows = (
  data: KpiExportData,
): Array<{
  case_id: string | number;
  status_name: string;
  received_date: string;
}> => {
  if (Array.isArray(data.case_list) && data.case_list.length > 0) {
    return data.case_list
      .filter(
        (row): row is {
          case_id: string | number;
          status_name?: string | null;
          received_date?: string | null;
        } => row.case_id !== undefined && row.case_id !== null,
      )
      .map((row) => ({
        case_id: row.case_id,
        status_name: row.status_name ?? "",
        received_date: row.received_date ?? "",
      }));
  }

  return getKpiCaseIds(data).map((caseId) => ({
    case_id: caseId,
    status_name: "",
    received_date: "",
  }));
};

export const buildKpiCaseListCsv = (data: KpiExportData): string => {
  const lines = ["Case ID,Case Status,Received Date"];
  getKpiCaseListRows(data).forEach((row) => {
    lines.push(
      [row.case_id, row.status_name, row.received_date].map(csvCell).join(","),
    );
  });
  return "\uFEFF" + lines.join("\r\n");
};

export const downloadKpiCaseList = (
  data: KpiExportData,
  title?: string,
) => {
  downloadBlob(
    new Blob([buildKpiCaseListCsv(data)], {
      type: "text/csv;charset=utf-8",
    }),
    `${exportFileName(data.label ?? title)}-case-list.csv`,
  );
};

export const downloadKpiCsv = (
  data: KpiExportData,
  title?: string,
  description?: string | null,
) => {
  downloadBlob(
    new Blob([buildKpiCsv(data, title, description)], {
      type: "text/csv;charset=utf-8",
    }),
    `${exportFileName(data.label ?? title)}.csv`,
  );
};

// ─── PNG / PDF ────────────────────────────────────────────────────────────────
//
// The image exports render a fresh copy of the widget into a hidden, fixed-
// width container and snapshot THAT — never the live dashboard tile. This is
// why a small tile still downloads a clean, full-size image: the export view
// lays out (and re-renders its chart) at EXPORT_WIDTH regardless of how small
// the widget is on screen.

// Fixed width the export view is rendered at (device-independent layout).
export const EXPORT_WIDTH = 760;

const snapshot = (el: HTMLElement): Promise<HTMLCanvasElement> =>
  html2canvas(el, {
    backgroundColor: "#ffffff",
    scale: 2,
    logging: false,
    useCORS: true,
  });

// Mount a React node off-screen at EXPORT_WIDTH, let it paint (and Highcharts
// draw its final, un-animated frame), snapshot it, then tear the mount down.
const captureNode = async (node: ReactNode): Promise<HTMLCanvasElement> => {
  const host = document.createElement("div");
  host.style.cssText =
    `position:fixed;top:0;left:-100000px;width:${EXPORT_WIDTH}px;` +
    `background:#ffffff;z-index:-1;pointer-events:none;`;
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(node as any);

  // Two rAFs for layout + a short beat so Highcharts finishes its synchronous
  // draw and web-fonts/icons are painted before we rasterize.
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setTimeout(resolve, 160)),
    ),
  );

  const canvas = await html2canvas(host, {
    backgroundColor: "#ffffff",
    scale: 2,
    logging: false,
    useCORS: true,
    width: EXPORT_WIDTH,
    windowWidth: EXPORT_WIDTH,
  });

  root.unmount();
  host.remove();
  return canvas;
};

const canvasToPng = (
  canvas: HTMLCanvasElement,
  data: KpiExportData,
  title?: string,
) =>
  triggerDownload(
    canvas.toDataURL("image/png"),
    `${exportFileName(data.label ?? title)}.png`,
  );

const canvasToPdf = (
  canvas: HTMLCanvasElement,
  data: KpiExportData,
  title?: string,
) => {
  const w = canvas.width / 2;
  const h = canvas.height / 2;
  const pdf = new jsPDF({
    orientation: w >= h ? "landscape" : "portrait",
    unit: "px",
    format: [w + 48, h + 72],
  });

  pdf.addImage(canvas.toDataURL("image/png"), "PNG", 24, 24, w, h);

  // Footer: formula + generated date, so the PDF stands on its own.
  pdf.setFontSize(9);
  pdf.setTextColor(100);
  const footerParts = [
    data.formula ? `Formula: ${data.formula}` : null,
    `Generated ${new Date().toLocaleDateString()}`,
  ].filter(Boolean);
  pdf.text(footerParts.join("  ·  "), 24, h + 48);

  pdf.save(`${exportFileName(data.label ?? title)}.pdf`);
};

// Preferred path: render a dedicated export view (crisp, fixed-size).
export const downloadNodePng = async (
  node: ReactNode,
  data: KpiExportData,
  title?: string,
) => canvasToPng(await captureNode(node), data, title);

export const downloadNodePdf = async (
  node: ReactNode,
  data: KpiExportData,
  title?: string,
) => canvasToPdf(await captureNode(node), data, title);

// Fallback path: snapshot a live element (used only when no export view is
// supplied). Small on-screen tile → small image, hence the node path above.
export const downloadKpiPng = async (
  el: HTMLElement,
  data: KpiExportData,
  title?: string,
) => canvasToPng(await snapshot(el), data, title);

export const downloadKpiPdf = async (
  el: HTMLElement,
  data: KpiExportData,
  title?: string,
) => canvasToPdf(await snapshot(el), data, title);
